import React, { useState } from "react";
import {
  Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, AreaChart, Area, RadialBarChart, RadialBar, PolarAngleAxis,
} from "recharts";
import { GraduationCap } from "lucide-react";
import { Bloc, Tableau, Cellule, Badge, Eyebrow } from "./ui";
import { argent } from "./api";

const TEINTES = ["#00C6CE", "#0F1B2D", "#F59E0B", "#7C3AED", "#EC4899", "#22C55E", "#3B82F6", "#EF4444", "#14B8A6", "#A16207"];

const Cadre = ({ titre, aide, children, testid }) => (
  <div className="bg-white rounded-2xl border p-5" style={{ borderColor: "var(--border)" }} data-testid={testid}>
    <Eyebrow>{titre}</Eyebrow>
    {aide && <p className="text-xs text-[var(--muted)] mt-1">{aide}</p>}
    <div className="mt-4" style={{ height: 240 }}>{children}</div>
  </div>
);

const infobulle = {
  contentStyle: { borderRadius: 12, border: "1px solid #E5E7EB", fontSize: 12, fontFamily: "Poppins" },
};

export function Graphiques({ s }) {
  const disciplines = [...s.par_discipline].filter((d) => d.eleves > 0).sort((a, b) => b.eleves - a.eleves);
  const tete = disciplines.slice(0, 8);
  const reste = disciplines.slice(8).reduce((n, d) => n + d.eleves, 0);
  const barres = reste ? [...tete, { cle: "Autres disciplines", eleves: reste }] : tete;
  const sources = s.par_source || [];
  const sourcesConnues = sources.filter((l) => l.cle !== "Non renseigné");
  /* Pourquoi ils partent : les motifs du formulaire, cumulés depuis le début. Les colonnes
     vides sont écartées du graphique (neuf motifs à zéro ne disent rien), sauf si personne
     n'est encore parti — on affiche alors le cadre avec son message. */
  const departs = (s.par_motif_depart || []).filter((l) => l.departs > 0)
    .sort((a, b) => b.departs - a.departs);
  const departsTotal = s.departs_total || 0;
  const pourcentEncaisse = s.argent.annuel.du ? Math.round((100 * s.argent.annuel.encaisse) / s.argent.annuel.du) : 0;

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <Cadre titre="Élèves par discipline" aide="Où sont réellement les élèves." testid="graphe-disciplines">
        <ResponsiveContainer>
          <BarChart data={barres} layout="vertical" barSize={14}
            margin={{ left: 8, right: 24, top: 4, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F6" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis type="category" dataKey="cle" width={130} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
            <Tooltip {...infobulle} formatter={(v) => [`${v} élèves`, "Élèves"]} />
            <Bar dataKey="eleves" name="Élèves" radius={[0, 4, 4, 0]}>
              {barres.map((d, i) => <Cell key={d.cle} fill={TEINTES[i % TEINTES.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Cadre>

      <Cadre titre="Places occupées et libres par jour" aide="Les barres courtes signalent les soirs à remplir." testid="graphe-jours">
        <ResponsiveContainer>
          <BarChart data={s.par_jour} barSize={22}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F6" vertical={false} />
            <XAxis dataKey="cle" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
            <Tooltip {...infobulle} />
            <Bar dataKey="eleves" name="Élèves" stackId="a" fill="#00C6CE" radius={[0, 0, 0, 0]} />
            <Bar dataKey="libres" name="Places libres" stackId="a" fill="#E2E8F0" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Cadre>

      <Cadre titre="Inscriptions semaine après semaine" aide="Le rythme de la rentrée, en cumulé." testid="graphe-semaines">
        <ResponsiveContainer>
          <AreaChart data={s.inscriptions_par_semaine}>
            <defs>
              <linearGradient id="degrade-inscriptions" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#00C6CE" stopOpacity={0.55} />
                <stop offset="100%" stopColor="#00C6CE" stopOpacity={0.04} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F6" vertical={false} />
            <XAxis dataKey="semaine" tick={{ fontSize: 10 }} axisLine={false} tickLine={false}
              tickFormatter={(v) => v.slice(5).replace("-", "/")} />
            <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
            <Tooltip {...infobulle} labelFormatter={(v) => `Semaine du ${v}`} />
            <Area type="monotone" dataKey="cumul" name="Cumul" stroke="#00C6CE" strokeWidth={2} fill="url(#degrade-inscriptions)" />
            <Area type="monotone" dataKey="inscriptions" name="Sur la semaine" stroke="#0F1B2D" strokeWidth={1.5} fillOpacity={0} />
          </AreaChart>
        </ResponsiveContainer>
      </Cadre>

      <Cadre titre="D'où viennent les inscriptions"
        aide={sourcesConnues.length
          ? "Ce que les familles ont indiqué à l'inscription, depuis le début — une famille compte une fois."
          : "La question n'est pas encore posée sur le site : seul le parrainage est connu aujourd'hui."}
        testid="graphe-sources">
        <ResponsiveContainer>
          <BarChart data={sources} layout="vertical" barSize={14}
            margin={{ left: 8, right: 24, top: 4, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F6" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis type="category" dataKey="cle" width={130} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
            <Tooltip {...infobulle} formatter={(v) => [`${v} inscription(s)`, "Inscriptions"]} />
            <Bar dataKey="inscriptions" name="Inscriptions" radius={[0, 4, 4, 0]}>
              {sources.map((l, i) => (
                <Cell key={l.cle} fill={l.cle === "Non renseigné" ? "#E2E8F0" : TEINTES[i % TEINTES.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Cadre>

      <Cadre titre="Pourquoi ils partent"
        aide={departsTotal
          ? `${departsTotal} départ(s) annoncé(s) depuis le début — les motifs du formulaire de désinscription.`
          : "Aucun départ annoncé pour l'instant. Les motifs du formulaire se cumuleront ici, saison après saison."}
        testid="graphe-departs">
        {departs.length ? (
          <ResponsiveContainer>
            <BarChart data={departs} layout="vertical" barSize={14}
              margin={{ left: 8, right: 24, top: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F6" horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="cle" width={150} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
              <Tooltip {...infobulle} formatter={(v) => [`${v} départ(s)`, "Départs"]} />
              <Bar dataKey="departs" name="Départs" radius={[0, 4, 4, 0]}>
                {departs.map((l, i) => (
                  <Cell key={l.cle}
                    fill={l.cle === "Non renseigné" ? "#E2E8F0"
                      : l.cle.startsWith("Insatisfaction") ? "#EF4444" : TEINTES[i % TEINTES.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-sm text-[var(--muted)]"
            data-testid="graphe-departs-vide">
            Personne n'a encore annoncé son départ cette saison.
          </div>
        )}
      </Cadre>


      <Cadre titre="Encaissement de l'année" aide={`${argent(s.argent.annuel.encaisse)} reçus sur ${argent(s.argent.annuel.du)} dus.`} testid="graphe-encaissement">
        <div className="relative h-full">
          <ResponsiveContainer>
            <RadialBarChart
              data={[{ nom: "Encaissé", valeur: pourcentEncaisse, fill: pourcentEncaisse < 50 ? "#F59E0B" : "#22C55E" }]}
              innerRadius="70%" outerRadius="100%" startAngle={210} endAngle={-30}
            >
              {/* Sans échelle explicite, une jauge à une seule valeur se dessine toujours pleine :
                  c'est ce qui donnait l'impression d'un diagramme figé. */}
              <PolarAngleAxis type="number" domain={[0, 100]} tick={false} axisLine={false} />
              <RadialBar dataKey="valeur" cornerRadius={12} background={{ fill: "#EEF2F6" }} />
            </RadialBarChart>
          </ResponsiveContainer>
          {/* Le chiffre est posé en HTML par-dessus la jauge : dans un <text> confié à
              recharts, le nombre disparaissait et seul le « % » restait visible. */}
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"
            style={{ paddingTop: 16 }} data-testid="graphe-encaissement-valeur">
            <div className="font-display" style={{ fontSize: 34, lineHeight: 1.1, color: "#0F1B2D" }}>
              {pourcentEncaisse} %
            </div>
            <div style={{ fontSize: 11, color: "#64748B" }}>encaissé sur l'année</div>
          </div>
        </div>
      </Cadre>
    </div>
  );
}

const ETATS = {
  surreservation: { ton: "rouge", label: "surréservé" },
  sous_effectif: { ton: "ambre", label: "moins de 4 élèves" },
  complet: { ton: "bleu", label: "complet" },
  ok: { ton: "vert", label: "correct" },
};

const FILTRES = [
  { id: "tous", label: "Tous" },
  { id: "sous_effectif", label: "Sous-effectif" },
  { id: "surreservation", label: "Surréservés" },
  { id: "complet", label: "Complets" },
];

export function ParCours({ cours, onOuvrir }) {
  const [filtre, setFiltre] = useState("tous");
  const lignes = filtre === "tous" ? cours : cours.filter((c) => c.etat === filtre);
  const compte = (id) => (id === "tous" ? cours.length : cours.filter((c) => c.etat === id).length);

  return (
    <Bloc
      Icon={GraduationCap}
      ton="analyses"
      titre="Les élèves par cours"
      aide="Rouge : moins de 4 élèves, le cours est en danger. Ambre : plus d'élèves que de places."
      testid="section-par-cours"
      action={
        <div className="flex gap-1 p-1 rounded-full" style={{ background: "#EEF2F6" }}>
          {FILTRES.map((f) => (
            <button
              key={f.id}
              data-testid={`par-cours-filtre-${f.id}`}
              onClick={() => setFiltre(f.id)}
              className="px-3 py-1.5 rounded-full text-xs font-semibold transition-colors"
              style={{
                background: filtre === f.id ? "#fff" : "transparent",
                color: filtre === f.id ? "var(--primary)" : "var(--muted)",
              }}
            >
              {f.label} ({compte(f.id)})
            </button>
          ))}
        </div>
      }
    >
      <Tableau
        testid="tableau-par-cours"
        onClic={onOuvrir}
        entetes={["Cours", "Quand", "Professeur", { label: "Inscrits", droite: true }, { label: "Attente", droite: true }, { label: "Places", droite: true }, "État"]}
        lignes={lignes}
        vide="Aucun cours dans cette vue."
        rendu={(c) => (
          <>
            <Cellule fort>
              {c.nom}
              <div className="text-xs font-normal text-[var(--muted)]">{c.discipline} · {c.salle}</div>
            </Cellule>
            <Cellule>{c.jour} {c.debut}</Cellule>
            <Cellule muet>{c.professeur}</Cellule>
            <Cellule droite fort>
              <span style={{ color: c.etat === "sous_effectif" ? "#B91C1C" : c.etat === "surreservation" ? "#B45309" : "inherit" }}>
                {c.inscrits}
              </span>
            </Cellule>
            <Cellule droite muet>{c.attente || "—"}</Cellule>
            <Cellule droite muet>{c.places || "—"}</Cellule>
            <Cellule><Badge ton={ETATS[c.etat].ton}>{ETATS[c.etat].label}</Badge></Cellule>
          </>
        )}
      />
    </Bloc>
  );
}
